import React, { useState, Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    setIsDesktop(mq.matches);
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

function PlayModal({ playUrl, title, isOpen, onClose }) {
  if (!playUrl) return null;
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto p-3 sm:p-4">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-sm transform overflow-hidden rounded-2xl gch-card p-3 shadow-xl transition-all sm:p-4">
                <div className="flex items-center justify-between mb-3">
                  <Dialog.Title className="text-lg font-semibold text-white">{title}</Dialog.Title>
                  <button type="button" onClick={onClose} className="gch-text-muted hover:text-white transition-colors" aria-label="Close">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <div className="mx-auto h-[min(72vh,640px)] w-full max-w-[360px] rounded-xl overflow-hidden bg-black">
                  <iframe src={playUrl} className="w-full h-full border-0" title={`Play ${title}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope" />
                </div>
                <a
                  href={playUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-white/10 py-2.5 text-xs font-semibold text-white/70 hover:text-white hover:border-white/25 transition-colors"
                >
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                  Play in new tab
                </a>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default function GameTemplateCard({
  id,
  badgeLabel,
  imageUrl,
  playUrl = null,
  primaryLabel,
  onPrimaryClick,
  primaryLoading = false,
  primaryLoadingLabel = '...',
  primaryIcon = null,
  className = '',
}) {
  const [playModalOpen, setPlayModalOpen] = useState(false);
  const [imageBroken, setImageBroken] = useState(false);
  const isDesktop = useIsDesktop();

  const handleCardClick = (e) => {
    // Don't trigger play if clicking any inner button
    if (e.target.closest('button')) return;
    if (!playUrl) return;
    if (isDesktop) {
      setPlayModalOpen(true);
    } else {
      window.open(playUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <div
        role={playUrl ? 'button' : undefined}
        tabIndex={playUrl ? 0 : undefined}
        onClick={handleCardClick}
        onKeyDown={(e) => e.key === 'Enter' && playUrl && handleCardClick(e)}
        className={`gch-card border border-white/10 hover:border-white/25 rounded-xl overflow-hidden flex flex-col group transition-all duration-200 hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gch-accent focus-visible:ring-offset-2 focus-visible:ring-offset-gch-bg ${playUrl ? 'cursor-pointer' : ''} ${className}`}
      >
        <div className="relative w-full aspect-[9/16] flex items-center justify-center overflow-hidden bg-white/5 shrink-0">
          {imageUrl && !imageBroken ? (
            <img
              src={imageUrl}
              alt=""
              loading="lazy"
              onError={() => setImageBroken(true)}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="flex flex-col items-center gap-1.5 gch-text-muted">
              <div className="text-4xl opacity-60" aria-hidden>🎮</div>
              {badgeLabel && <div className="text-[10px] font-semibold uppercase tracking-wider opacity-70">{badgeLabel}</div>}
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
          {badgeLabel && (
            <span className="absolute left-2 top-2 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-black/60 text-white/95 backdrop-blur-sm">
              {badgeLabel}
            </span>
          )}
        </div>
        <div className="p-3 w-full">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPrimaryClick?.();
            }}
            disabled={primaryLoading}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg py-2 px-3 text-xs font-semibold border border-white/10 text-white/80 hover:border-white/25 hover:text-white hover:bg-white/5 transition-colors duration-200 disabled:opacity-60 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gch-accent focus-visible:ring-offset-2 focus-visible:ring-offset-gch-bg"
          >
            {primaryLoading ? (
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="h-3 w-3 rounded-full border-2 border-white/50 border-t-transparent animate-spin"
                  aria-hidden="true"
                />
                <span>{primaryLoadingLabel}</span>
              </span>
            ) : (
              <>
                {primaryIcon}
                {primaryLabel}
              </>
            )}
          </button>
        </div>
      </div>

      <PlayModal
        playUrl={playUrl}
        title={badgeLabel || 'Play'}
        isOpen={playModalOpen}
        onClose={() => setPlayModalOpen(false)}
      />
    </>
  );
}
